import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { AgendaItemModel } from '../../../models/agenda-item.model';
import { OptionalType } from '../../../models/types/optional.type';
import { AlertService } from '../../../services/alert.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { AgendaItemImportVotesComponent } from '../../dialogs/agenda-item-import-votes/agenda-item-import-votes.component';
import { AgendaItemSettingsComponent } from '../../dialogs/agenda-item-settings/agenda-item-settings.component';

@Component({
    selector: 'sl-general-meeting-agenda',
    templateUrl: './general-meeting-agenda.component.html',
    styleUrls: ['./general-meeting-agenda.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralMeetingAgendaComponent extends BaseComponent implements OnInit {
    readonly agendaItems = signal<AgendaItemModel[]>([]);

    readonly gmAgendaItemNumberStartingVote = signal<OptionalType<number>>(undefined);
    readonly gmAgendaItemNumberClosingVote = signal<OptionalType<number>>(undefined);

    private readonly dialog = inject(MatDialog);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly alertService = inject(AlertService);

    ngOnInit(): void {
        this.loadItems();
    }

    startVote(agendaNumber: number): void {
        this.gmAgendaItemNumberStartingVote.set(agendaNumber);
        this.generalMeetingsResource
            .startVotingOnAgendaItem(agendaNumber)
            .pipe(
                finalize(() => {
                    this.loadItems();
                    this.gmAgendaItemNumberStartingVote.set(undefined);
                })
            )
            .subscribe();
    }

    closeVote(agendaNumber: number): void {
        this.gmAgendaItemNumberClosingVote.set(agendaNumber);
        this.generalMeetingsResource
            .closeVotingOnAgendaItem(agendaNumber)
            .pipe(
                finalize(() => {
                    this.loadItems();
                    this.gmAgendaItemNumberClosingVote.set(undefined);
                })
            )
            .subscribe();
    }

    deleteVote(agendaNumber: number): void {
        this.alertService.showConfirm('GeneralMeeting.AreYouSureReset', 'GeneralMeeting.AreYouSureResetTitle', () => {
            this.generalMeetingsResource
                .deleteVotingOnAgendaItem(agendaNumber)
                .pipe(finalize(() => this.loadItems()))
                .subscribe();
        });
    }

    viewSettings(agendaNumber: number): void {
        const dialogRef = this.dialog.open(AgendaItemSettingsComponent, {
            data: {
                agendaItemNumber: agendaNumber
            },
            height: 'auto',
            maxHeight: '98svh',
            maxWidth: '98vw'
        });
        dialogRef
            .afterClosed()
            .pipe(finalize(() => this.loadItems()))
            .subscribe();
    }

    duplicateAgendaItem(agendaItemNumber: number): void {
        this.generalMeetingsResource
            .duplicateAgendaItem(agendaItemNumber)
            .pipe(finalize(() => this.loadItems()))
            .subscribe();
    }

    deleteAgendaItem(agendaItemNumber: number): void {
        // double confirmation for deleting an agenda item to prevent accidental deletion
        this.alertService.showConfirm('GeneralMeeting.AreYouSureRemoveAgendaItem', undefined, () => {
            this.alertService.showConfirm(
                'GeneralMeeting.AreYouSureRemoveAgendaItem2Text',
                'GeneralMeeting.AreYouSureRemoveAgendaItem2Title',
                () => {
                    this.generalMeetingsResource
                        .deleteAgendaItem(agendaItemNumber)
                        .pipe(finalize(() => this.loadItems()))
                        .subscribe();
                }
            );
        });
    }

    openImportVotesDialog(agendaItem: AgendaItemModel): void {
        const dialogRef = this.dialog.open(AgendaItemImportVotesComponent, {
            data: { agendaItem },
            width: '720px',
            disableClose: true
        });

        this.addSubscriptions(
            dialogRef.afterClosed().subscribe(shouldRefresh => {
                if (shouldRefresh) {
                    this.loadItems();
                }
            })
        );
    }

    isParent(element: AgendaItemModel): boolean {
        return this.agendaItems().some(item => item.parentGmAgendaItemNumber === element.gmAgendaItemNumber);
    }

    accept(agendaNumber: number): void {
        this.generalMeetingsResource
            .acceptVoting(agendaNumber)
            .pipe(finalize(() => this.loadItems()))
            .subscribe();
    }

    undoAccept(agendaNumber: number): void {
        this.generalMeetingsResource
            .undoAcceptVoting(agendaNumber)
            .pipe(finalize(() => this.loadItems()))
            .subscribe();
    }

    private loadItems(): void {
        this.generalMeetingsResource.getCurrentAgendaItems().subscribe(r => {
            const agendaItems: AgendaItemModel[] = [];

            r.value.forEach(e => {
                const agendaItem = new AgendaItemModel(e);
                agendaItems.push(agendaItem);
            });

            this.agendaItems.set(agendaItems);

            this.agendaItems().forEach(i => {
                if (!i.parentGmAgendaItemNumber) {
                    return;
                }

                const parent = this.agendaItems().find(p => p.gmAgendaItemNumber === i.parentGmAgendaItemNumber);
                if (!parent) {
                    return;
                }

                parent.hasChildren = true;
                parent.childrenCount++;

                if (i.isVotingOpen) {
                    parent.childrenVoteOpen.push(i.gmAgendaItemNumber);
                }

                if (!i.votingStartedDateTime) {
                    parent.childrenNotStarted.push(i.gmAgendaItemNumber);
                }

                if (!!i.votingEndedDateTime) {
                    parent.childrenVoteFinished.push(i.gmAgendaItemNumber);

                    if (!!i.resultAcceptedTime) {
                        parent.childrenVoteAccepted.push(i.gmAgendaItemNumber);
                    } else {
                        parent.childrenVoteNotAccepted.push(i.gmAgendaItemNumber);
                    }
                }
            });

            const agendaItemsSortedByEndedTime = this.agendaItems()
                .filter(i => !!i.votingEndedDateTime)
                .sort((a, b) => (b.votingEndedDateTime?.getTime() ?? 0) - (a.votingEndedDateTime?.getTime() ?? 0));
            const lastClosedItem = agendaItemsSortedByEndedTime[0];

            if (!!lastClosedItem) {
                lastClosedItem.showResults = true;
            }
        });
    }
}
